/**
 * 项目详情页首屏并行加载：文件夹 + 条目列表 + 权限 + 聚合统计（Task 9.6）
 */
import { ref } from 'vue'

import type { Project } from '@/modules/project/api/project'
import {
  deriveProjectFolderAccess,
  type ProjectFolderAccess,
} from '@/modules/project/api/project-access'
import { getProject } from '@/modules/project/api/project'
import {
  aggregateWorkItemStatusCounts,
  getWorkItems,
  type WorkItem,
} from '@/modules/project/api/work-item'
import { runApiBatch } from '@/shared/api/use-api-batch'

export interface ProjectDetailBundle {
  project: Project | null
  workItems: WorkItem[]
  statusCounts: Record<string, number>
  access: ProjectFolderAccess
}

export async function loadProjectDetailBundle(
  projectId: number,
  userId: number | null,
): Promise<ProjectDetailBundle> {
  const results = await runApiBatch<unknown>([
    (ctx) => getProject(projectId, ctx.requestConfig),
    (ctx) => getWorkItems(projectId, ctx.requestConfig),
  ])

  const folder =
    results[0].status === 'fulfilled' ? (results[0].value as Project) : null
  const itemsResult =
    results[1].status === 'fulfilled'
      ? (results[1].value as { items: WorkItem[] })
      : { items: [] as WorkItem[] }
  const workItems = itemsResult.items ?? []

  return {
    project: folder,
    workItems,
    statusCounts: aggregateWorkItemStatusCounts(workItems),
    access: deriveProjectFolderAccess(folder, userId),
  }
}

export function useProjectDetailLoader() {
  const loading = ref(false)
  const project = ref<Project | null>(null)
  const workItems = ref<WorkItem[]>([])
  const statusCounts = ref<Record<string, number>>({})
  const access = ref<ProjectFolderAccess>(deriveProjectFolderAccess(null, null))

  async function loadProjectDetail(
    projectId: number,
    userId: number | null,
  ): Promise<ProjectDetailBundle> {
    loading.value = true
    try {
      const bundle = await loadProjectDetailBundle(projectId, userId)
      project.value = bundle.project
      workItems.value = bundle.workItems
      statusCounts.value = bundle.statusCounts
      access.value = bundle.access
      return bundle
    } finally {
      loading.value = false
    }
  }

  function reset() {
    project.value = null
    workItems.value = []
    statusCounts.value = {}
    access.value = deriveProjectFolderAccess(null, null)
  }

  return {
    loading,
    project,
    workItems,
    statusCounts,
    access,
    loadProjectDetail,
    reset,
  }
}
